// Modal "Enviar contato" do atendimento (abre pelo "+" do MessageInput), no
// padrão Aeros Dark: casca AerosDialog + estilos compartilhados de
// styles/aerosModalStyles. A busca de contatos, o atalho de criar contato novo
// pelo termo digitado e o envio do vCard são os mesmos da versão anterior.
import React, { useState, useEffect } from "react";

import { makeStyles, alpha } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import PersonAddOutlinedIcon from "@material-ui/icons/PersonAddOutlined";
import SendIcon from "@material-ui/icons/Send";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    option: { display: "flex", alignItems: "center", gap: 10, minWidth: 0, width: "100%" },
    optionAvatar: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      fontSize: 11,
      fontWeight: 700,
      color: "#07080D",
      backgroundColor: AEROS.cyan,
    },
    optionName: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    optionNumber: { fontSize: 11.5, color: theme.palette.text.secondary },
    optionAdd: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12.5,
      fontWeight: 600,
      color: stateText(theme, AEROS.cyan, "info"),
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    selected: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      borderRadius: 12,
      minWidth: 0,
    },
    empty: {
      padding: "10px 14px",
      border: `1px dashed ${border}`,
      borderRadius: 12,
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
  };
});

const filter = createFilterOptions({
  trim: true,
});

const ContactSendModal = ({ modalOpen, onClose }) => {
  const m = useAerosModalStyles();
  const classes = useStyles();
  const [options, setOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [newContact, setNewContact] = useState({});

  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam, channel: "whatsapp", isGroup: "false" },
          });

          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedContact(null);
  };

  const handleSendVcard = async contact => {
    if (!contact) return;

    setLoading(true);
    try {
      onClose(contact);
    } catch (err) {
    }
    setLoading(false);
  };

  const handleSelectOption = (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
    } else if (newValue?.name) {
      setNewContact({ name: newValue.name });
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = contact => {
    handleSendVcard(contact);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const inicial = (nome) => (nome || "?").trim().charAt(0).toUpperCase();

  const renderOption = option => {
    if (option.number) {
      return (
        <div className={classes.option}>
          <span className={classes.optionAvatar}>{inicial(option.name)}</span>
          <div style={{ minWidth: 0 }}>
            <div className={classes.optionName}>{option.name}</div>
            <div className={classes.optionNumber}>{option.number}</div>
          </div>
        </div>
      );
    }
    return (
      <span className={classes.optionAdd}>
        <PersonAddOutlinedIcon />
        {`${i18n.t("SendContactModal.add")} ${option.name}`}
      </span>
    );
  };

  const renderOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  return (
    <>
      <ContactModal
        open={contactModalOpen}
        initialValues={newContact}
        onClose={handleCloseContactModal}
        onSave={handleAddNewContactTicket}
      ></ContactModal>
      <AerosDialog
        open={modalOpen}
        onClose={handleClose}
        width={520}
        title={i18n.t("SendContactModal.title")}
        subtitle={i18n.t("aeros.sendContact.subtitle")}
        onSubmit={() => handleSendVcard(selectedContact)}
        submitLabel={i18n.t("SendContactModal.buttons.ok")}
        submitIcon={<SendIcon />}
        submitDisabled={!selectedContact}
        cancelLabel={i18n.t("SendContactModal.buttons.cancel")}
        loading={loading}
      >
        <div>
          <label className={m.fieldLabel}>{i18n.t("SendContactModal.fieldLabel")}</label>
          <Autocomplete
            fullWidth
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={(e, newValue) => handleSelectOption(e, newValue)}
            renderInput={params => (
              <TextField
                {...params}
                className={m.input}
                placeholder={i18n.t("aeros.sendContact.placeholder")}
                variant="outlined"
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                onKeyPress={e => {
                  if (loading || !selectedContact) return;
                  else if (e.key === "Enter") {
                    handleSendVcard(selectedContact);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          <div className={m.hint}>{i18n.t("aeros.sendContact.hint")}</div>
        </div>

        {selectedContact ? (
          <div className={classes.selected}>
            <span className={classes.optionAvatar}>{inicial(selectedContact.name)}</span>
            <div style={{ minWidth: 0 }}>
              <div className={classes.optionName}>{selectedContact.name}</div>
              <div className={classes.optionNumber}>{selectedContact.number}</div>
            </div>
          </div>
        ) : (
          <div className={classes.empty}>{i18n.t("aeros.sendContact.none")}</div>
        )}
      </AerosDialog>
    </>
  );
};
export default ContactSendModal;
